import { useCallback, useRef, useSyncExternalStore } from 'react';

import {
  nextSitePromoBannerBoundary,
  resolveSitePromoBanner,
  type SitePromoBannerCampaign,
} from '@opendoor/shared-ts/site-promo-banner';

const MAX_TIMEOUT_MS = 2_147_483_647;
const DISMISS_EVENT = 'opendoor:site-promo-banner-dismissed';
const getServerSnapshot = () => false;
const STAGING_SITE_HOST = /^(?:pr-\d+-)?(?:beacon|cosmos)\.apps-staging\.opendoor\.com$/;
const LOCAL_SITE_HOST = /^www\.\d+\.localhost$/;

const dismissalKey = (id: string) => `opendoor.site-promo-banner.dismissed.${id}`;

function isSitePromoBannerHost(hostname: string): boolean {
  return (
    hostname === 'www.opendoor.com' ||
    hostname === 'opendoor.com' ||
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    LOCAL_SITE_HOST.test(hostname) ||
    STAGING_SITE_HOST.test(hostname)
  );
}

function wasDismissed(id: string): boolean {
  try {
    return window.localStorage.getItem(dismissalKey(id)) === '1';
  } catch {
    return false;
  }
}

type SitePromoBannerOptions = {
  campaign: SitePromoBannerCampaign | null;
  pathname: string | null;
  articleKeywords?: readonly string[];
};

export function useSitePromoBanner({
  campaign,
  pathname,
  articleKeywords,
}: SitePromoBannerOptions): { campaign: SitePromoBannerCampaign | null; dismiss: () => void } {
  const dismissedId = useRef<string | null>(null);
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (!campaign || !isSitePromoBannerHost(window.location.hostname)) return () => {};

      let timer: number | undefined;
      const schedule = () => {
        window.clearTimeout(timer);
        const nowMs = Date.now();
        const next = nextSitePromoBannerBoundary(campaign, nowMs);
        if (next !== null) {
          timer = window.setTimeout(
            () => {
              onChange();
              schedule();
            },
            Math.max(1, Math.min(next - nowMs, MAX_TIMEOUT_MS)),
          );
        }
      };
      const onStorage = (event: StorageEvent) => {
        if (event.key === dismissalKey(campaign.id)) onChange();
      };
      schedule();
      window.addEventListener('focus', onChange);
      window.addEventListener('storage', onStorage);
      window.addEventListener(DISMISS_EVENT, onChange);
      return () => {
        window.clearTimeout(timer);
        window.removeEventListener('focus', onChange);
        window.removeEventListener('storage', onStorage);
        window.removeEventListener(DISMISS_EVENT, onChange);
      };
    },
    [campaign],
  );

  const getSnapshot = useCallback(
    () =>
      !!campaign &&
      isSitePromoBannerHost(window.location.hostname) &&
      dismissedId.current !== campaign.id &&
      !wasDismissed(campaign.id) &&
      resolveSitePromoBanner({ campaign, pathname, nowMs: Date.now(), articleKeywords }) !== null,
    [campaign, pathname, articleKeywords],
  );
  const visible = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const dismiss = useCallback(() => {
    if (!campaign) return;
    dismissedId.current = campaign.id;
    try {
      window.localStorage.setItem(dismissalKey(campaign.id), '1');
    } catch {
      // Closing still works in browsers that block storage.
    }
    window.dispatchEvent(new Event(DISMISS_EVENT));
  }, [campaign]);

  return { campaign: visible ? campaign : null, dismiss };
}
